import Vue from 'vue'
import { onMounted } from '@vue/composition-api'
import { useStorage } from '@vueuse/core'
import { merge } from 'lodash'
import { DisplayMode } from '@/views/v2-temp/report/aggregation-container/composables/useAggregationResultTable'

interface UserPreferences {
  eod_closing: {
    neverAfterJobEditWarning: boolean
    neverIsPastWarning: boolean
  }
  'eod_closing:expenses': {
    showTertiaryColumns: boolean
  }
  'employee_management:users': {
    viewMode: 'grid' | 'list'
  }
  'report:aggregation_containers': {
    displayModes: {
      [aggregationContainerId: string]: {
        aggregations: {
          [aggregationId: string]: DisplayMode,
        },
      },
    },
    displaySumRows: {
      [aggregationContainerId: string]: {
        aggregations: {
          [aggregationId: string]: boolean,
        },
      },
    },
  }
}

const LS_KEY = 'myAppUserPreferences'

// デフォルト値
const DEFAULT_PREFERENCES: UserPreferences = {
  eod_closing: {
    neverAfterJobEditWarning: false,
    neverIsPastWarning: false,
  },
  'eod_closing:expenses': {
    showTertiaryColumns: false,
  },
  'employee_management:users': {
    viewMode: 'grid',
  },
  'report:aggregation_containers': {
    displayModes: {},
    displaySumRows: {},
  },
}

// migration関数
const migratePreferences = (storedData: any): UserPreferences => {
  return merge({}, DEFAULT_PREFERENCES, storedData)
}

export default () => {
  const preferences = useStorage<UserPreferences>(
    LS_KEY,
    DEFAULT_PREFERENCES,
    localStorage,
    { deep: true },
  )

  onMounted(() => {
    const stored = localStorage.getItem(LS_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)

        // migrationを実行して新しいキーを追加
        const migratedData = migratePreferences(parsed)
        preferences.value = migratedData

        // 更新されたデータをlocalStorageに保存
        localStorage.setItem(LS_KEY, JSON.stringify(migratedData))
      } catch (e) {
        // JSONパース失敗などは無視
        console.warn('Failed to parse stored preferences:', e)
      }
    } else {
      localStorage.setItem(LS_KEY, JSON.stringify(preferences.value))
    }
  })

  const setDisplayMode = (containerId: string, aggregationId: string, mode: DisplayMode) => {
    const dm = preferences.value['report:aggregation_containers'].displayModes

    // containerId がまだなければ Vue.set で追加
    if (!dm[containerId]) {
      Vue.set(dm, containerId, { aggregations: {} })
    }

    // aggregations 自身も新規キー追加なので Vue.set
    const aggs = dm[containerId].aggregations
    Vue.set(aggs, aggregationId, mode)
  }

  const getDisplayMode = (containerId: string, aggregationId: string): string => {
    return preferences.value['report:aggregation_containers'].displayModes[containerId]?.aggregations[aggregationId]
  }

  const setDisplaySumRow = (containerId: string, aggregationId: string, display: boolean) => {
    const dsr = preferences.value['report:aggregation_containers'].displaySumRows

    // containerId がまだなければ Vue.set で追加
    if (!dsr[containerId]) {
      Vue.set(dsr, containerId, { aggregations: {} })
    }

    // aggregations 自身も新規キー追加なので Vue.set
    const aggs = dsr[containerId].aggregations
    Vue.set(aggs, aggregationId, display)
  }

  const getDisplaySumRow = (containerId: string, aggregationId: string): boolean => {
    const dsr = preferences.value['report:aggregation_containers'].displaySumRows

    return dsr[containerId]?.aggregations[aggregationId] ?? false
  }

  return {
    preferences,
    setDisplayMode,
    getDisplayMode,
    setDisplaySumRow,
    getDisplaySumRow,
  }
}
